import { useEffect, useState, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { fetchAdminRequests, updateAdminRequest } from '../api';

// Admin Requests queue. Staff triage member service requests (cleaning crew,
// groundskeeper, clay range, bounce house, etc.): change status and leave notes.
// Open + in-progress float to the top (server-ordered).

const TYPE_LABELS = {
  cleaning: 'Cleaning crew',
  groundskeeper: 'Groundskeeper',
  clay_range: 'Clay range',
  bounce_house: 'Bounce house',
  other: 'Other',
};

const STATUSES = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'done', label: 'Done' },
  { value: 'declined', label: 'Declined' },
];

const FILTERS = [
  { value: '', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'done', label: 'Done' },
  { value: 'declined', label: 'Declined' },
];

function fmtDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d).slice(0, 10);
  return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function AdminRequests() {
  const { instance } = useMsal();
  const account = instance.getActiveAccount();

  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [noteDrafts, setNoteDrafts] = useState({}); // id -> draft note
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchAdminRequests(instance, account, filter || undefined);
      if (res.ok) setRequests(res.data.requests || []);
      else setError(res.data?.message || 'Could not load requests.');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [instance, account, filter]);

  useEffect(() => { load(); }, [load]);

  const patch = async (id, body) => {
    setSavingId(id);
    try {
      const res = await updateAdminRequest(instance, account, id, body);
      if (res.ok && res.data?.request) {
        setRequests((rs) => rs.map((r) => (r.id === id ? { ...r, ...res.data.request } : r)));
      } else {
        alert(res.data?.message || 'Could not update the request.');
      }
    } finally {
      setSavingId(null);
    }
  };

  const setStatus = (id, status) => patch(id, { status });
  const saveNote = (id) => patch(id, { adminNotes: noteDrafts[id] ?? '' });

  return (
    <div className="card">
      <h2>Member Requests</h2>
      <p className="muted">Service requests from members — cleaning, groundskeeper, event setups, and more.</p>

      <div className="filter-row">
        {FILTERS.map((f) => (
          <button key={f.value}
            className={`chip${filter === f.value ? ' is-active' : ''}`}
            onClick={() => setFilter(f.value)}>
            {f.label}
          </button>
        ))}
      </div>

      {error && <p className="error-text">{error}</p>}
      {loading ? <p className="muted">Loading…</p> : requests.length === 0 ? (
        <p className="muted">No requests{filter ? ` (${filter})` : ''}.</p>
      ) : (
        <ul className="req-list">
          {requests.map((r) => (
            <li key={r.id} className={`req-card req-card-${r.status}`}>
              <div className="req-card-head">
                <div>
                  <strong>{TYPE_LABELS[r.request_type] || r.request_type}</strong>
                  <span className={`req-status req-${r.status}`}>
                    {STATUSES.find((s) => s.value === r.status)?.label || r.status}
                  </span>
                </div>
                <span className="muted">{fmtDate(r.created_at)}</span>
              </div>

              <div className="req-card-body">
                <div className="req-member">
                  {r.member_name || 'Member'}
                  {r.member_email && <span className="muted"> · {r.member_email}</span>}
                  {r.member_phone && <span className="muted"> · {r.member_phone}</span>}
                </div>
                {r.requested_date && <div className="muted">Requested for: {fmtDate(r.requested_date)}</div>}
                {r.spot_name && (
                  <div className="muted">
                    Stay: {r.spot_name}
                    {r.res_start && ` (${fmtDate(r.res_start)} → ${fmtDate(r.res_end)})`}
                  </div>
                )}
                {r.details && <p className="req-details">{r.details}</p>}
              </div>

              <div className="req-card-actions">
                <label className="req-status-select">
                  Status
                  <select value={r.status} disabled={savingId === r.id}
                    onChange={(e) => setStatus(r.id, e.target.value)}>
                    {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </label>
                <div className="req-note-row">
                  <input type="text" placeholder="Add a note for this request…"
                    value={noteDrafts[r.id] ?? r.admin_notes ?? ''}
                    onChange={(e) => setNoteDrafts({ ...noteDrafts, [r.id]: e.target.value })} />
                  <button disabled={savingId === r.id} onClick={() => saveNote(r.id)}>Save note</button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
